import React, { useState, useMemo } from "react";
import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  onSnapshot,
  serverTimestamp,
  query,
  orderBy,
} from "firebase/firestore";
import { db } from "../firebase";

// ─── Constants ───────────────────────────────────────────────────────────────

const RISK_TYPES = [
  "Climate", "Geological", "Biological", "ET", "Social",
  "Political", "Environmental", "Financial", "Cultural",
  "Technical", "Health", "Managerial", "Procure", "Crime", "Legal",
];

const RESPONSE_TYPES = ["Prevention", "Reduction", "Transference", "Contingency", "Acceptance"];

const OWNERSHIP_CATEGORIES = [
  "Employer / Client", "Supplier/Service Provider/Contractor",
  "Professional Team", "Contractor", "Critical Role Player",
  "Internal Stakeholder", "External Stakeholder",
  "Local Authority", "Government Department",
];

const STATUSES = ["Logged", "Analysis", "Decision", "Allocated", "Action", "Escalated", "Closed", "Occurred"];

const RATING_LABELS = {
  low: { label: "LOW", bg: "#dcfce7", text: "#15803d", border: "#86efac" },
  medium: { label: "MEDIUM", bg: "#fef9c3", text: "#a16207", border: "#fde047" },
  high: { label: "HIGH", bg: "#fee2e2", text: "#b91c1c", border: "#fca5a5" },
};

const STATUS_COLORS = {
  Logged:    { bg: "#f1f5f9", text: "#475569" },
  Analysis:  { bg: "#dbeafe", text: "#1d4ed8" },
  Decision:  { bg: "#fef3c7", text: "#d97706" },
  Allocated: { bg: "#ede9fe", text: "#7c3aed" },
  Action:    { bg: "#cffafe", text: "#0e7490" },
  Escalated: { bg: "#fee2e2", text: "#dc2626" },
  Closed:    { bg: "#f0fdf4", text: "#16a34a" },
  Occurred:  { bg: "#1e293b", text: "#f8fafc" },
};

function getRating(likelihood, severity) {
  const l = Number(likelihood) || 1;
  const s = Number(severity) || 1;
  const score = (l + s) / 2;
  if (l >= 4 && s >= 4) return "high";
  if (l <= 2 && s <= 2) return "low";
  if (score >= 4) return "high";
  if (score >= 3) return "medium";
  return "low";
}

const emptyRisk = {
  type: "",
  author: "",
  dateRaised: "",
  dateUpdated: "",
  riskEvent: "",
  impactDescription: "",
  likelihood: 1,
  severity: 1,
  riskOwnershipCategory: "",
  riskOwner: "",
  riskResponse: "",
  riskResponseType: "",
  contingencyMeasure: "",
  dueDate: "",
  status: "Logged",
  progressNotes: "",
};

// ─── Sub-components ──────────────────────────────────────────────────────────

function RatingBadge({ likelihood, severity, size = "sm" }) {
  const rating = getRating(likelihood, severity);
  const c = RATING_LABELS[rating];
  const score = ((Number(likelihood) || 1) * (Number(severity) || 1));
  const pad = size === "lg" ? "6px 14px" : "3px 10px";
  const fontSize = size === "lg" ? 13 : 11;
  return (
    <span style={{
      background: c.bg, color: c.text, border: `1px solid ${c.border}`,
      borderRadius: 20, padding: pad, fontSize, fontWeight: 700,
      letterSpacing: "0.05em", whiteSpace: "nowrap",
    }}>
      {c.label} ({score})
    </span>
  );
}

function StatusBadge({ status }) {
  const c = STATUS_COLORS[status] || STATUS_COLORS.Logged;
  return (
    <span style={{
      background: c.bg, color: c.text, borderRadius: 20,
      padding: "3px 10px", fontSize: 11, fontWeight: 600, whiteSpace: "nowrap",
    }}>
      {status}
    </span>
  );
}

function RiskMatrixCell({ likelihood, severity, active }) {
  const rating = getRating(likelihood, severity);
  const c = RATING_LABELS[rating];
  return (
    <div style={{
      width: 36, height: 36, display: "flex", alignItems: "center", justifyContent: "center",
      background: active ? c.text : c.bg,
      color: active ? "#fff" : c.text,
      borderRadius: 6, fontSize: 10, fontWeight: active ? 800 : 500,
      border: active ? `2px solid ${c.text}` : `1px solid ${c.border}`,
      transition: "all 0.15s",
      cursor: "default",
    }}>
      {active ? "●" : `${likelihood}×${severity}`}
    </div>
  );
}

function RiskMatrix({ risks }) {
  const counts = {};
  risks.forEach(r => {
    const k = `${r.likelihood}-${r.severity}`;
    counts[k] = (counts[k] || 0) + 1;
  });

  return (
    <div>
      <div style={{ fontSize: 11, color: "#94a3b8", marginBottom: 8, fontWeight: 600, letterSpacing: "0.05em" }}>
        RISK MATRIX (LIKELIHOOD × SEVERITY)
      </div>
      <div style={{ display: "flex", alignItems: "flex-end", gap: 4 }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 2, marginRight: 4 }}>
          {[5,4,3,2,1].map(s => (
            <div key={s} style={{ height: 36, display: "flex", alignItems: "center", fontSize: 10, color: "#94a3b8", width: 14, justifyContent: "center", fontWeight: 600 }}>{s}</div>
          ))}
          <div style={{ height: 20, fontSize: 9, color: "#94a3b8", textAlign: "center" }}>SEV</div>
        </div>
        <div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 36px)", gap: 2 }}>
            {[5,4,3,2,1].flatMap(s =>
              [1,2,3,4,5].map(l => {
                const count = counts[`${l}-${s}`] || 0;
                const rating = getRating(l, s);
                const c = RATING_LABELS[rating];
                return (
                  <div key={`${l}-${s}`} style={{
                    width: 36, height: 36, display: "flex", alignItems: "center", justifyContent: "center",
                    background: count > 0 ? c.text : c.bg,
                    color: count > 0 ? "#fff" : c.text,
                    borderRadius: 6, fontSize: count > 0 ? 13 : 9, fontWeight: count > 0 ? 800 : 400,
                    border: `1px solid ${c.border}`,
                  }}>
                    {count > 0 ? count : ""}
                  </div>
                );
              })
            )}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 36px)", gap: 2, marginTop: 2 }}>
            {[1,2,3,4,5].map(l => (
              <div key={l} style={{ textAlign: "center", fontSize: 10, color: "#94a3b8", fontWeight: 600 }}>{l}</div>
            ))}
          </div>
          <div style={{ fontSize: 9, color: "#94a3b8", textAlign: "center", marginTop: 2 }}>LIKELIHOOD →</div>
        </div>
      </div>
    </div>
  );
}

function FieldInput({ label, value, onChange, type = "text", options, required, multiline, placeholder }) {
  const base = {
    width: "100%", padding: "8px 10px", borderRadius: 8,
    border: "1.5px solid #e2e8f0", background: "#f8fafc",
    fontSize: 13, color: "#0f172a", outline: "none", boxSizing: "border-box",
    fontFamily: "inherit", transition: "border 0.15s",
  };
  if (options) {
    return (
      <div style={{ marginBottom: 12 }}>
        {label && <div style={{ fontSize: 11, fontWeight: 600, color: "#64748b", marginBottom: 4, letterSpacing: "0.04em" }}>{label}{required && " *"}</div>}
        <select value={value} onChange={e => onChange(e.target.value)} style={{ ...base, cursor: "pointer" }}>
          <option value="">— select —</option>
          {options.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
      </div>
    );
  }
  if (multiline) {
    return (
      <div style={{ marginBottom: 12 }}>
        {label && <div style={{ fontSize: 11, fontWeight: 600, color: "#64748b", marginBottom: 4, letterSpacing: "0.04em" }}>{label}{required && " *"}</div>}
        <textarea value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
          rows={3} style={{ ...base, resize: "vertical", lineHeight: 1.5 }} />
      </div>
    );
  }
  return (
    <div style={{ marginBottom: 12 }}>
      {label && <div style={{ fontSize: 11, fontWeight: 600, color: "#64748b", marginBottom: 4, letterSpacing: "0.04em" }}>{label}{required && " *"}</div>}
      <input type={type} value={value} onChange={e => onChange(e.target.value)}
        placeholder={placeholder} style={base} />
    </div>
  );
}

function ScoreSlider({ label, value, onChange }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
        <span style={{ fontSize: 11, fontWeight: 600, color: "#64748b", letterSpacing: "0.04em" }}>{label}</span>
        <span style={{ fontSize: 18, fontWeight: 800, color: "#0f172a", minWidth: 24, textAlign: "right" }}>{value}</span>
      </div>
      <div style={{ display: "flex", gap: 4 }}>
        {[1,2,3,4,5].map(n => (
          <button key={n} onClick={() => onChange(n)} style={{
            flex: 1, height: 32, borderRadius: 8, border: "none", cursor: "pointer",
            background: Number(value) >= n ? "#0f172a" : "#e2e8f0",
            color: Number(value) >= n ? "#fff" : "#94a3b8",
            fontSize: 12, fontWeight: 700, transition: "all 0.1s",
          }}>{n}</button>
        ))}
      </div>
    </div>
  );
}

// ─── Modal ────────────────────────────────────────────────────────────────────

function RiskModal({ risk, onSave, onClose, mode }) {
  const [form, setForm] = useState(risk || emptyRisk);

  const set = (field) => (val) => setForm(f => ({ ...f, [field]: val }));

  const rating = getRating(form.likelihood, form.severity);
  const c = RATING_LABELS[rating];

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 1000,
      background: "rgba(15,23,42,0.6)", backdropFilter: "blur(4px)",
      display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
    }} onClick={onClose}>
      <div style={{
        background: "#fff", borderRadius: 18, width: "100%", maxWidth: 760,
        maxHeight: "90vh", overflow: "hidden", display: "flex", flexDirection: "column",
        boxShadow: "0 32px 80px rgba(15,23,42,0.24)",
      }} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={{
          padding: "20px 28px", borderBottom: "1px solid #f1f5f9",
          display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0,
        }}>
          <div>
            <div style={{ fontSize: 17, fontWeight: 800, color: "#0f172a" }}>
              {mode === "add" ? "Log New Risk" : "Edit Risk"}
            </div>
            <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 2 }}>Contract Risk Register</div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{
              padding: "6px 16px", borderRadius: 12,
              background: c.bg, color: c.text, fontSize: 13, fontWeight: 700,
              border: `1px solid ${c.border}`,
            }}>
              {c.label} — Score {(Number(form.likelihood) || 1) * (Number(form.severity) || 1)}
            </div>
            <button onClick={onClose} style={{
              background: "#f1f5f9", border: "none", borderRadius: 10,
              width: 34, height: 34, cursor: "pointer", fontSize: 16, color: "#64748b",
            }}>✕</button>
          </div>
        </div>

        {/* Body */}
        <div style={{ padding: "20px 28px", overflowY: "auto", flex: 1 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 24px" }}>
            <FieldInput label="Risk Event" value={form.riskEvent} onChange={set("riskEvent")} required multiline placeholder="Describe the risk event…" />
            <FieldInput label="Impact Description" value={form.impactDescription} onChange={set("impactDescription")} required multiline placeholder="What is the potential impact?" />
            <FieldInput label="Risk Type" value={form.type} onChange={set("type")} options={RISK_TYPES} />
            <FieldInput label="Status" value={form.status} onChange={set("status")} options={STATUSES} />
            <FieldInput label="Author / Source" value={form.author} onChange={set("author")} placeholder="e.g. Site Engineer" />
            <FieldInput label="Date Raised" value={form.dateRaised} onChange={set("dateRaised")} type="date" />
            <div style={{ gridColumn: "1 / -1", display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 24px" }}>
              <ScoreSlider label="LIKELIHOOD (1 = Unlikely → 5 = Almost Certain)" value={form.likelihood} onChange={set("likelihood")} />
              <ScoreSlider label="SEVERITY (1 = Negligible → 5 = Catastrophic)" value={form.severity} onChange={set("severity")} />
            </div>
            <FieldInput label="Risk Ownership Category" value={form.riskOwnershipCategory} onChange={set("riskOwnershipCategory")} options={OWNERSHIP_CATEGORIES} />
            <FieldInput label="Risk Owner" value={form.riskOwner} onChange={set("riskOwner")} placeholder="Name or department" />
            <FieldInput label="Response Type" value={form.riskResponseType} onChange={set("riskResponseType")} options={RESPONSE_TYPES} />
            <FieldInput label="Due Date" value={form.dueDate} onChange={set("dueDate")} type="date" />
            <FieldInput label="Risk Response / Countermeasure" value={form.riskResponse} onChange={set("riskResponse")} multiline placeholder="Describe the solution or countermeasure…" />
            <FieldInput label="Contingency Measure" value={form.contingencyMeasure} onChange={set("contingencyMeasure")} multiline placeholder="Plan of action if risk occurs…" />
            <div style={{ gridColumn: "1 / -1" }}>
              <FieldInput label="Progress Notes" value={form.progressNotes} onChange={set("progressNotes")} multiline placeholder="Latest progress update…" />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div style={{
          padding: "16px 28px", borderTop: "1px solid #f1f5f9",
          display: "flex", justifyContent: "flex-end", gap: 10, flexShrink: 0,
        }}>
          <button onClick={onClose} style={{
            padding: "9px 22px", borderRadius: 10, border: "1.5px solid #e2e8f0",
            background: "#fff", color: "#475569", fontSize: 13, fontWeight: 600, cursor: "pointer",
          }}>Cancel</button>
          <button onClick={() => onSave(form)} style={{
            padding: "9px 26px", borderRadius: 10, border: "none",
            background: "#0f172a", color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer",
          }}>
            {mode === "add" ? "Log Risk" : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Risk Card ────────────────────────────────────────────────────────────────

function RiskCard({ risk, index, onEdit, onDelete }) {
  const [expanded, setExpanded] = useState(false);
  const rating = getRating(risk.likelihood, risk.severity);
  const c = RATING_LABELS[rating];

  return (
    <div style={{
      background: "#fff", borderRadius: 14, border: `1px solid #e2e8f0`,
      borderLeft: `4px solid ${c.text}`,
      marginBottom: 8, overflow: "hidden",
      boxShadow: "0 1px 4px rgba(15,23,42,0.06)",
      transition: "box-shadow 0.15s",
    }}>
      <div
        onClick={() => setExpanded(e => !e)}
        style={{ padding: "14px 18px", cursor: "pointer", display: "flex", alignItems: "flex-start", gap: 14 }}
      >
        {/* ID */}
        <div style={{
          flexShrink: 0, width: 32, height: 32, borderRadius: 10,
          background: "#f1f5f9", display: "flex", alignItems: "center",
          justifyContent: "center", fontSize: 12, fontWeight: 800, color: "#475569",
        }}>
          {index + 1}
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 4 }}>
            <span style={{ fontSize: 14, fontWeight: 700, color: "#0f172a" }}>{risk.riskEvent || "Untitled Risk"}</span>
            {risk.type && (
              <span style={{ fontSize: 10, background: "#f1f5f9", color: "#64748b", padding: "2px 8px", borderRadius: 20, fontWeight: 600 }}>{risk.type}</span>
            )}
          </div>
          <div style={{ fontSize: 12, color: "#64748b", lineHeight: 1.5, marginBottom: 6 }}>
            {risk.impactDescription || "No impact description"}
          </div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
            <RatingBadge likelihood={risk.likelihood} severity={risk.severity} />
            <StatusBadge status={risk.status} />
            {risk.riskResponseType && (
              <span style={{ fontSize: 11, color: "#64748b", background: "#f8fafc", border: "1px solid #e2e8f0", padding: "2px 8px", borderRadius: 20 }}>
                {risk.riskResponseType}
              </span>
            )}
            {risk.riskOwner && (
              <span style={{ fontSize: 11, color: "#64748b" }}>👤 {risk.riskOwner}</span>
            )}
          </div>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
          <button onClick={e => { e.stopPropagation(); onEdit(risk); }} style={{
            background: "#f1f5f9", border: "none", borderRadius: 8, padding: "6px 12px",
            fontSize: 12, color: "#475569", cursor: "pointer", fontWeight: 600,
          }}>Edit</button>
          <button onClick={e => { e.stopPropagation(); onDelete(risk.id); }} style={{
            background: "#fee2e2", border: "none", borderRadius: 8, padding: "6px 10px",
            fontSize: 12, color: "#dc2626", cursor: "pointer", fontWeight: 600,
          }}>✕</button>
          <span style={{ fontSize: 16, color: "#94a3b8", transform: expanded ? "rotate(180deg)" : "none", transition: "transform 0.2s" }}>▾</span>
        </div>
      </div>

      {expanded && (
        <div style={{ padding: "0 18px 16px 18px", borderTop: "1px solid #f1f5f9" }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, paddingTop: 14 }}>
            {risk.riskResponse && (
              <div style={{ gridColumn: "1 / -1", background: "#f8fafc", borderRadius: 10, padding: "10px 14px" }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: "#94a3b8", marginBottom: 4, letterSpacing: "0.06em" }}>RISK RESPONSE</div>
                <div style={{ fontSize: 13, color: "#334155" }}>{risk.riskResponse}</div>
              </div>
            )}
            {risk.contingencyMeasure && (
              <div style={{ gridColumn: "1 / -1", background: "#fefce8", borderRadius: 10, padding: "10px 14px" }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: "#a16207", marginBottom: 4, letterSpacing: "0.06em" }}>CONTINGENCY</div>
                <div style={{ fontSize: 13, color: "#713f12" }}>{risk.contingencyMeasure}</div>
              </div>
            )}
            {risk.progressNotes && (
              <div style={{ gridColumn: "1 / -1", background: "#f0fdf4", borderRadius: 10, padding: "10px 14px" }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: "#15803d", marginBottom: 4, letterSpacing: "0.06em" }}>PROGRESS NOTES</div>
                <div style={{ fontSize: 13, color: "#14532d" }}>{risk.progressNotes}</div>
              </div>
            )}
            <InfoChip label="Ownership" value={risk.riskOwnershipCategory} />
            <InfoChip label="L×S Score" value={`${risk.likelihood} × ${risk.severity} = ${(Number(risk.likelihood)||1)*(Number(risk.severity)||1)}`} />
            <InfoChip label="Due Date" value={risk.dueDate} />
          </div>
        </div>
      )}
    </div>
  );
}

function InfoChip({ label, value }) {
  if (!value) return null;
  return (
    <div style={{ background: "#f8fafc", borderRadius: 10, padding: "8px 12px" }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: "#94a3b8", marginBottom: 2, letterSpacing: "0.06em" }}>{label.toUpperCase()}</div>
      <div style={{ fontSize: 13, color: "#334155", fontWeight: 500 }}>{value}</div>
    </div>
  );
}

// ─── Summary Cards ────────────────────────────────────────────────────────────

function SummaryCard({ label, value, color, sub }) {
  return (
    <div style={{
      background: "#fff", borderRadius: 14, padding: "18px 20px",
      border: "1px solid #e2e8f0", flex: 1, minWidth: 120,
    }}>
      <div style={{ fontSize: 11, color: "#94a3b8", fontWeight: 600, letterSpacing: "0.06em", marginBottom: 8 }}>{label}</div>
      <div style={{ fontSize: 30, fontWeight: 900, color: color || "#0f172a", lineHeight: 1 }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 6 }}>{sub}</div>}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function RiskSection({ currentProjectId }) {
  const [risks, setRisks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // null | { mode: "add"|"edit", risk }
  const [filterRating, setFilterRating] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [search, setSearch] = useState("");
  const [view, setView] = useState("list"); // "list" | "matrix"

  // Firestore subscription
  React.useEffect(() => {
    if (!currentProjectId) { setLoading(false); return; }
    const q = query(
      collection(db, "projects", currentProjectId, "risks"),
      orderBy("createdAt", "asc")
    );
    const unsub = onSnapshot(q, snap => {
      setRisks(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    }, () => setLoading(false));
    return unsub;
  }, [currentProjectId]);

  // Stats
  const stats = useMemo(() => {
    const high = risks.filter(r => getRating(r.likelihood, r.severity) === "high").length;
    const medium = risks.filter(r => getRating(r.likelihood, r.severity) === "medium").length;
    const low = risks.filter(r => getRating(r.likelihood, r.severity) === "low").length;
    const open = risks.filter(r => !["Closed", "Occurred"].includes(r.status)).length;
    return { high, medium, low, open, total: risks.length };
  }, [risks]);

  const filtered = useMemo(() => {
    return risks.filter(r => {
      if (filterRating !== "all" && getRating(r.likelihood, r.severity) !== filterRating) return false;
      if (filterStatus !== "all" && r.status !== filterStatus) return false;
      if (search) {
        const q = search.toLowerCase();
        return (r.riskEvent || "").toLowerCase().includes(q) ||
               (r.impactDescription || "").toLowerCase().includes(q) ||
               (r.riskOwner || "").toLowerCase().includes(q);
      }
      return true;
    });
  }, [risks, filterRating, filterStatus, search]);

  async function handleSave(form) {
    if (!currentProjectId) return;
    const data = { ...form, updatedAt: serverTimestamp() };
    if (modal.mode === "add") {
      await addDoc(collection(db, "projects", currentProjectId, "risks"), {
        ...data, createdAt: serverTimestamp(),
      });
    } else {
      await updateDoc(doc(db, "projects", currentProjectId, "risks", modal.risk.id), data);
    }
    setModal(null);
  }

  async function handleDelete(id) {
    if (!currentProjectId || !window.confirm("Delete this risk? This cannot be undone.")) return;
    await deleteDoc(doc(db, "projects", currentProjectId, "risks", id));
  }

  const pillStyle = (active) => ({
    padding: "6px 16px", borderRadius: 20, border: "none", cursor: "pointer", fontSize: 12,
    fontWeight: 600, background: active ? "#0f172a" : "#f1f5f9",
    color: active ? "#fff" : "#64748b", transition: "all 0.15s",
  });

  if (!currentProjectId) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: 300, color: "#94a3b8", fontSize: 15 }}>
        Select a project to view the Risk Register
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 1100, margin: "0 auto", padding: "28px 20px" }}>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
              <div style={{ width: 38, height: 38, borderRadius: 12, background: "#0f172a", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18 }}>⚠</div>
              <div>
                <h1 style={{ margin: 0, fontSize: 22, fontWeight: 900, color: "#0f172a" }}>Risk Register</h1>
                <div style={{ fontSize: 12, color: "#94a3b8" }}>Contract Risk Management Log</div>
              </div>
            </div>
          </div>
          <button onClick={() => setModal({ mode: "add", risk: null })} style={{
            padding: "10px 22px", borderRadius: 12, border: "none",
            background: "#0f172a", color: "#fff", fontSize: 13, fontWeight: 700,
            cursor: "pointer", display: "flex", alignItems: "center", gap: 8,
          }}>
            + Log Risk
          </button>
        </div>
      </div>

      {/* Summary */}
      <div style={{ display: "flex", gap: 10, marginBottom: 20, flexWrap: "wrap" }}>
        <SummaryCard label="TOTAL RISKS" value={stats.total} sub={`${stats.open} open`} />
        <SummaryCard label="HIGH" value={stats.high} color="#b91c1c" sub="Immediate action" />
        <SummaryCard label="MEDIUM" value={stats.medium} color="#a16207" sub="Monitor closely" />
        <SummaryCard label="LOW" value={stats.low} color="#15803d" sub="Accept / watch" />
        <div style={{ flex: 2, minWidth: 200 }}>
          <div style={{ background: "#fff", borderRadius: 14, padding: "18px 20px", border: "1px solid #e2e8f0", height: "100%", boxSizing: "border-box" }}>
            <RiskMatrix risks={risks} />
          </div>
        </div>
      </div>

      {/* Filters */}
      <div style={{
        background: "#fff", borderRadius: 14, padding: "14px 18px",
        border: "1px solid #e2e8f0", marginBottom: 16,
        display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap",
      }}>
        <input
          value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search risks…"
          style={{
            flex: "1 1 180px", padding: "8px 12px", borderRadius: 10,
            border: "1.5px solid #e2e8f0", fontSize: 13, outline: "none", background: "#f8fafc",
          }}
        />
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {["all","high","medium","low"].map(r => (
            <button key={r} onClick={() => setFilterRating(r)} style={pillStyle(filterRating === r)}>
              {r === "all" ? "All Ratings" : r.charAt(0).toUpperCase() + r.slice(1)}
            </button>
          ))}
        </div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          <button onClick={() => setFilterStatus("all")} style={pillStyle(filterStatus === "all")}>All Status</button>
          {["Action","Escalated","Closed"].map(s => (
            <button key={s} onClick={() => setFilterStatus(s)} style={pillStyle(filterStatus === s)}>{s}</button>
          ))}
        </div>
        <div style={{ fontSize: 12, color: "#94a3b8", marginLeft: "auto" }}>
          {filtered.length} of {risks.length} risks
        </div>
      </div>

      {/* List */}
      {loading ? (
        <div style={{ textAlign: "center", padding: 60, color: "#94a3b8" }}>Loading risk register…</div>
      ) : filtered.length === 0 ? (
        <div style={{
          textAlign: "center", padding: "60px 20px", background: "#fff",
          borderRadius: 14, border: "1px solid #e2e8f0", color: "#94a3b8",
        }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>🛡</div>
          <div style={{ fontSize: 15, fontWeight: 600 }}>No risks logged yet</div>
          <div style={{ fontSize: 13, marginTop: 6 }}>Click "Log Risk" to add the first risk to this register</div>
        </div>
      ) : (
        <div>
          {filtered.map((risk, i) => (
            <RiskCard key={risk.id} risk={risk} index={risks.indexOf(risk)} onEdit={r => setModal({ mode: "edit", risk: r })} onDelete={handleDelete} />
          ))}
        </div>
      )}

      {/* Legend */}
      <div style={{ marginTop: 24, display: "flex", gap: 8, flexWrap: "wrap" }}>
        {Object.entries(RATING_LABELS).map(([k, c]) => (
          <div key={k} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: "#64748b" }}>
            <div style={{ width: 10, height: 10, borderRadius: 3, background: c.text }} />
            {c.label}
          </div>
        ))}
        <span style={{ color: "#cbd5e1", fontSize: 11 }}>|</span>
        <span style={{ fontSize: 11, color: "#94a3b8" }}>Risk Score = Likelihood × Severity</span>
      </div>

      {/* Modal */}
      {modal && (
        <RiskModal
          risk={modal.risk}
          mode={modal.mode}
          onSave={handleSave}
          onClose={() => setModal(null)}
        />
      )}
    </div>
  );
}
